import { useState, useCallback } from "react"
import { useQuery, useMutation } from "@apollo/client/react"
import {
  GetConversationsDocument,
  CreateConversationDocument,
  DeleteConversationDocument,
} from "@/graphql/main/gql/graphql"
import type { ChatConversation, ChatMessage } from "@/graphql/main/gql/graphql"
import { createLogger } from "@/model/logger"

const logger = createLogger("useChatConversations")

export const useChatConversations = (isOpen: boolean, showConversationsList: boolean, isEligible: boolean) => {
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null)
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set())

  const {
    data: conversationsData,
    loading: conversationsLoading,
    refetch: refetchConversations,
  } = useQuery(GetConversationsDocument, {
    variables: { ids: null },
    skip: !isEligible || (!isOpen && !showConversationsList),
  })

  const [createConversation] = useMutation(CreateConversationDocument)
  const [deleteConversation] = useMutation(DeleteConversationDocument)

  const handleCreateConversation = useCallback(async () => {
    try {
      const { data } = await createConversation()
      if (data?.chatCreateConversation.id) {
        const conversationId = data.chatCreateConversation.id
        logger.debug("New conversation created", { conversationId })
        setSelectedConversation(conversationId)
        return conversationId
      }
      logger.warn("Failed to create conversation - no ID returned")
    } catch (error) {
      logger.error("Error creating conversation", error)
    }
    return null
  }, [createConversation])

  const handleDeleteConversation = useCallback(
    async (id: string) => {
      try {
        logger.debug("Deleting conversation", { conversationId: id })
        setDeletingIds((prev) => new Set([...prev, id]))
        await deleteConversation({
          variables: { id },
          refetchQueries: [{ query: GetConversationsDocument }],
        })
        if (id === selectedConversation) {
          setSelectedConversation(null)
        }
        logger.debug("Conversation deleted successfully", { conversationId: id })
      } catch (error) {
        logger.error("Error deleting conversation", error)
      } finally {
        setDeletingIds((prev) => {
          const newSet = new Set(prev)
          newSet.delete(id)
          return newSet
        })
      }
    },
    [deleteConversation, selectedConversation],
  )

  const sortConversations = useCallback((conversations: readonly ChatConversation[]) => {
    return [...conversations].sort((a, b) => {
      const aLastMessage = a.messages[a.messages.length - 1]?.createdAt || a.createdAt
      const bLastMessage = b.messages[b.messages.length - 1]?.createdAt || b.createdAt
      return new Date(bLastMessage).getTime() - new Date(aLastMessage).getTime()
    })
  }, [])

  const getChatTitle = useCallback((messages: readonly ChatMessage[], defaultTitle: string) => {
    const firstMessage = messages.find((msg: ChatMessage) => msg.text.trim())
    if (!firstMessage) return defaultTitle

    const firstLine = (firstMessage.text.split("\n")[0] ?? "").trim()
    return firstLine.length > 30 ? `${firstLine.substring(0, 30)}...` : firstLine
  }, [])

  return {
    selectedConversation,
    setSelectedConversation,
    conversationsData,
    conversationsLoading,
    refetchConversations,
    handleCreateConversation,
    handleDeleteConversation,
    deletingIds,
    sortConversations,
    getChatTitle,
  }
}
